import {
  ClickAwayListener,
  IconButton,
  ListItemButton,
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  AutocompleteRenderGetTagProps,
  AutocompleteRenderInputParams,
  InputBase,
  Autocomplete as MuiAutocomplete,
  Popper,
  PopperProps,
  styled,
  Chip,
} from '@mui/material'
import {
  CSSProperties,
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'
import { colorPalette } from '../../../style/colorPalette'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import { CloseRounded } from '@mui/icons-material'
import { SelectOption } from '../../../model/selectOption'
import { MenuItem, MenuItemText } from '../../select'

type SelectValue = string
type MultiSelectProps = {
  value: SelectValue[] | undefined
  options: SelectOption[]
  // onChange is optional in case it is disabled.
  onChange?: (v: SelectValue[] | undefined) => void
  disabled?: boolean
  clearable?: boolean
  renderOption?: (v: SelectOption) => JSX.Element
} & StyleProps
type StyleProps = {}
export const MultiSelect = ({
  value,
  options,
  onChange,
  disabled = false,
  clearable = false,
  renderOption: _renderOption,
}: MultiSelectProps) => {
  const onChangeValue = useCallback(
    (_, v: SelectOption[], reason: AutocompleteChangeReason) => {
      onChange && onChange(v ? v.map(v => v.value) : undefined)
    },
    [onChange]
  )
  const renderInput = useCallback((params: AutocompleteRenderInputParams) => {
    return <TextInput {...params.InputProps} inputProps={params.inputProps} />
  }, [])
  const renderOption = useCallback((props, option: SelectOption) => {
    return (
      <MenuItem {...props}>
        {_renderOption ? (
          _renderOption(option)
        ) : (
          <MenuItemText>{option.name}</MenuItemText>
        )}
      </MenuItem>
    )
  }, [])
  const isOptionEqualToValue = useCallback(
    (option: SelectOption, v: SelectOption) => {
      return option.value === v.value
    },
    []
  )
  const getOptionLabel = useCallback((option: SelectOption) => {
    return option.name
  }, [])

  const selectedValues = useMemo(() => {
    if (!value || !options) return
    return options.filter(o => value.includes(o.value))
  }, [value, options])

  const renderTags = useCallback(
    (v: SelectOption[], getTagProps: AutocompleteRenderGetTagProps) =>
      v.map((v, index) => (
        <Chip
          size="small"
          label={v.name}
          {...getTagProps({ index })}
          key={getTagProps({ index }).key}
        />
      )),
    []
  )

  return (
    <MuiAutocomplete
      // Use sx props since making Autocomplete styled component takes a bit effort.
      sx={{ width: '100%' }}
      multiple={true}
      value={selectedValues}
      onChange={onChangeValue}
      isOptionEqualToValue={isOptionEqualToValue}
      options={options}
      renderInput={renderInput}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      disableCloseOnSelect={true}
      PopperComponent={PopperComponent}
      popupIcon={<PopupIcon />}
      clearIcon={<CloseIcon />}
      disabled={disabled}
      disableClearable={!clearable}
      renderTags={renderTags}
    />
  )
}

const PopperComponent = memo((props: PopperProps) => (
  <Popper {...props} placement="bottom-start" />
))
const TextInput = styled(InputBase)({
  width: '100%',
  color: colorPalette.monotone[10],
})
const PopupIcon = styled(ExpandMoreRoundedIcon)({
  color: colorPalette.monotone[4],
})
const CloseIcon = styled(CloseRounded)({
  color: colorPalette.monotone[4],
  fontSize: '14px',
})
